import { toLocalMidnightForCalendarDate } from './date';
import {
  findNonBillableDefaultJob,
  NonBillableJobLike,
} from './foreman-non-billable-default-job.utils';

/**
 * How far ahead an employee may book VAC / SICK / HOL on Individual Time Entry.
 *
 * Tim, 2026-07-27, verbatim: "Four weeks". This is the single written home for that number;
 * both the web page and the NativeScript page read it rather than each hard-coding a bound.
 *
 * Scope: this file owns ONLY the future side. The existing minus-seven-day past bound is a
 * separate rule and deliberately does not live here, so extending this one cannot silently
 * start governing backdating.
 */
export const FOREMAN_LEAVE_ADVANCE_WEEKS = 4;

/** Four work weeks expressed in calendar days. */
export const FOREMAN_LEAVE_ADVANCE_DAYS = FOREMAN_LEAVE_ADVANCE_WEEKS * 7;

/**
 * The latest calendar day an employee may select, as a local midnight.
 *
 * 🔴 Calendar arithmetic (`setDate`), never `getTime() + n * 86400000`. Millisecond math across
 * a DST boundary lands an hour short and can resolve to the PREVIOUS calendar day, which on a
 * date picker is an off-by-one bound that only misbehaves twice a year. The existing
 * minus-seven-day bounds on both ITE pages still use the millisecond form and carry that same
 * latent defect; they are out of scope here but worth converging later.
 */
export function maxLeaveEntryDate(today: Date = new Date()): Date {
  // `toLocalMidnightForCalendarDate` returns null for an invalid Date. Fall back to the current
  // day rather than propagating null: this value bounds a picker, and a null max would render
  // as "no upper bound", which is the failure direction this whole rule exists to remove.
  const base =
    toLocalMidnightForCalendarDate(today) ??
    toLocalMidnightForCalendarDate(new Date()) ??
    new Date();
  base.setDate(base.getDate() + FOREMAN_LEAVE_ADVANCE_DAYS);
  return base;
}

/**
 * Whether a selected date is within the advance window.
 *
 * Compares on the LOCAL CALENDAR DAY, not the instant, so a 23:59 selection on the final day is
 * still the final day. Fails CLOSED on anything that is not a real Date, because the control can
 * hand back null before the user picks and a string from a persisted draft, and coercing either
 * would skip the bound entirely.
 *
 * Past dates pass: this predicate bounds only the future side (see the note on the constant).
 */
export function isWithinLeaveAdvanceWindow(
  selected: Date | null | undefined,
  today: Date = new Date()
): boolean {
  if (!(selected instanceof Date) || Number.isNaN(selected.getTime())) {
    return false;
  }
  const selectedDay = toLocalMidnightForCalendarDate(selected);
  if (selectedDay === null) {
    return false;
  }
  return selectedDay.getTime() <= maxLeaveEntryDate(today).getTime();
}

/**
 * Whether a selected date falls in the LEAVE-ONLY region: strictly after today.
 *
 * Tim, 2026-07-27: four weeks ahead is for "vacation, sick and holiday only. No add jobs, only
 * their non bill job and phase." So the advance window and the leave-only restriction are the
 * same region, and this predicate is what both pages gate on.
 *
 * 🔴 Raising a page's date cap WITHOUT gating on this would let people book ordinary billable
 * work four weeks out, which is worse than shipping nothing. The two changes belong together.
 *
 * TODAY IS NOT LEAVE-ONLY. Today keeps its full existing behaviour (all jobs, add-job, every
 * earn code); only genuinely future days are restricted. Fails CLOSED (treated as leave-only)
 * on anything that is not a real Date, so an unparseable value cannot unlock add-job.
 */
export function isLeaveOnlyDate(
  selected: Date | null | undefined,
  today: Date = new Date()
): boolean {
  if (!(selected instanceof Date) || Number.isNaN(selected.getTime())) {
    return true;
  }
  const selectedDay = toLocalMidnightForCalendarDate(selected);
  const todayDay =
    toLocalMidnightForCalendarDate(today) ??
    toLocalMidnightForCalendarDate(new Date());
  if (selectedDay === null || todayDay === null) {
    return true;
  }
  return selectedDay.getTime() > todayDay.getTime();
}

/**
 * The jobs a user may enter time against on a leave-only day: their non-billable default job
 * alone, or NOTHING when it cannot be identified.
 *
 * 🔴 FAILS CLOSED, and this is the load-bearing choice rather than a defensive habit. Falling
 * back to the full list when the NB job is unknown would turn a future date back into an open
 * window on ordinary billable work, which is precisely what the four-week cap must not create.
 * An empty list is visibly wrong to the user and gets reported; a full one silently permits
 * exactly what Tim excluded ("no add jobs, only their non bill job and phase") and nothing
 * downstream would ever contradict it.
 *
 * Callers must gate on {@link isLeaveOnlyDate} first. This function does NOT check the date,
 * so it stays a pure list operation that both platforms can test without a clock.
 */
export function leaveOnlyJobList<T extends NonBillableJobLike>(jobs: T[]): T[] {
  if (!Array.isArray(jobs)) {
    return [];
  }
  const nbJob = findNonBillableDefaultJob(jobs, null);
  return nbJob ? [nbJob] : [];
}

export interface ForemanLeaveOnlyHours {
  hw?: number | null;
  st?: number | null;
  ot?: number | null;
  pt?: number | null;
  pd?: number | null;
  trv?: number | null;
  vac?: number | null;
  sick?: number | null;
  hol?: number | null;
}

/**
 * Forward-dated Individual Time Entry is VAC / SICK / HOL only. Keep the leave
 * buckets and clear every worked/travel/expense bucket before the mobile editor
 * writes draft labors, so hidden controls cannot leak stale values into submit.
 */
export function toLeaveOnlyHoursWorked<T extends ForemanLeaveOnlyHours>(
  hours: T
): T {
  return {
    ...hours,
    hw: 0,
    st: 0,
    ot: 0,
    pt: 0,
    pd: 0,
    trv: 0,
  };
}
